import { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { LEVEL_CONFIG, getLevelName, getMasteryLabel, getReadingStage } from '../../../config/levelConfig.js';

const MASTERY_COLORS = {
  amber: '#F59E0B',
  teal: '#14B8A6',
  green: '#16A34A'
};

function startOfDay(ts) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function formatDay(ts) {
  const today = startOfDay(Date.now());
  if (ts === today) return 'Today';
  if (ts === today - 86400000) return 'Yesterday';
  return new Date(ts).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function groupedJourney() {
  const groups = [];
  for (const row of LEVEL_CONFIG) {
    const last = groups[groups.length - 1];
    if (!last || last.name !== row.name) {
      groups.push({ name: row.name, minLevel: row.level, maxLevel: row.level, phase: row.ehriPhase });
    } else {
      last.maxLevel = row.level;
    }
  }
  return groups;
}

function groupSessions(log = []) {
  const sessions = new Map();
  for (const entry of log) {
    if (!entry?.t) continue;
    const day = startOfDay(entry.t);
    if (!sessions.has(day)) sessions.set(day, []);
    sessions.get(day).push(entry);
  }
  return [...sessions.entries()]
    .map(([day, entries]) => {
      const sorted = entries.slice().sort((a, b) => a.t - b.t);
      const first = sorted[0]?.t || day;
      const last = sorted[sorted.length - 1]?.t || day;
      const spanMin = Math.max(1, Math.round((last - first) / 60000));
      const modeCounts = sorted.reduce((acc, e) => {
        if (!e.m) return acc;
        acc[e.m] = (acc[e.m] || 0) + 1;
        return acc;
      }, {});
      const correct = sorted.filter((e) => e.c).length;
      const accuracy = sorted.length ? Math.round((correct / sorted.length) * 100) : 0;
      return { day, first, last, minutes: Math.max(1, Math.min(spanMin + Math.round(sorted.length * 0.25), sorted.length * 3)), entries: sorted, modeCounts, correct, attempted: sorted.length, accuracy };
    })
    .sort((a, b) => b.day - a.day);
}

function getModeMastery(tracker = {}) {
  const ms = tracker.ms || {};
  const modes = [
    { key: 'phonics', label: 'Phonics', icon: '🔤' },
    { key: 'sight', label: 'Sight Words', icon: '👁️' },
    { key: 'stories', label: 'Stories', icon: '📖' },
    { key: 'bubbles', label: 'Comprehension / Bubble Pop', icon: '🫧' }
  ];
  return modes.map((mode) => {
    const stats = ms[mode.key] || { c: 0, t: 0 };
    const probability = stats.t > 0 ? stats.c / stats.t : 0.5;
    const mastery = getMasteryLabel(probability);
    return { ...mode, probability, mastery };
  });
}

export function ProgressTab({ profile, onStartSession }) {
  const [showHow, setShowHow] = useState(false);
  const [expanded, setExpanded] = useState({});
  const tracker = profile?.tracker || {};
  const level = tracker.currentLevel || 1;
  const stage = getReadingStage(level);
  const history = tracker.levelHistory || [];
  const sessions = useMemo(() => groupSessions(tracker.log || []), [tracker.log]);
  const modeMastery = useMemo(() => getModeMastery(tracker), [tracker]);
  const journey = useMemo(() => groupedJourney(), []);
  const hasSessions = sessions.length > 0;

  const stageLevels = LEVEL_CONFIG.filter((row) => row.ehriPhase === (stage ? LEVEL_CONFIG.find((r) => r.level === level)?.ehriPhase : null));
  const stagePosition = stageLevels.findIndex((row) => row.level === level);
  const levelInStage = stagePosition >= 0 ? stagePosition + 1 : 1;
  const stageCount = stageLevels.length || 1;
  const stagePct = Math.round((levelInStage / stageCount) * 100);

  const reachedDateByLevel = history.reduce((acc, item) => {
    if (item?.level && item?.date && !acc[item.level]) acc[item.level] = item.date;
    return acc;
  }, {});

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 py-5">
      <div className="mb-4 rounded-finley-xl border-2 border-finley-primary/20 bg-finley-primary/5 p-5 shadow-finley-sm">
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="rounded-finley-full bg-finley-primary/10 px-3 py-1 text-xs font-semibold text-finley-primary">{stage?.stageName || 'Reading Stage'}</span>
          <button onClick={() => setShowHow((v) => !v)} className="h-7 w-7 rounded-finley-full border border-finley-text-muted/30 text-xs font-semibold text-finley-text-light">?</button>
        </div>
        <p className="mb-2 text-sm leading-6 text-finley-text-light">{stage?.description || 'Finley adjusts every session to support your child’s reading growth.'}</p>
        <div className="text-sm text-finley-text"><strong>Currently:</strong> {getLevelName(level)}</div>
        <div className="mt-2 text-xs text-finley-text-light">Level {levelInStage} of {stageCount} in this stage</div>
        <div className="mt-1 h-2 overflow-hidden rounded-finley-full bg-finley-bg-card">
          <div className="h-full rounded-finley-full bg-finley-primary" style={{ width: `${stagePct}%` }} />
        </div>

        {showHow && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="mt-3 rounded-finley-lg border border-finley-text-muted/15 bg-finley-bg-card p-4 text-sm leading-6 text-finley-text-light">
            <p className="mb-2">Finley tracks over 40 reading skills and adjusts every session to focus on what your child needs most. This is called adaptive learning.</p>
            <p className="mb-2">Words your child is learning follow a spaced review schedule. Words they know well are reviewed less often. Words they're still learning come back more frequently.</p>
            <p className="m-0">Finley's approach is based on the Science of Reading, which maps how children naturally progress from recognizing letters to reading fluently.</p>
          </motion.div>
        )}
      </div>

      <div className="mb-4 rounded-finley-xl border-2 border-finley-secondary/20 bg-finley-bg-card p-5 shadow-finley-sm">
        <div className="mb-3 font-finley-display text-xl text-finley-text">Level Journey</div>
        <div className="space-y-3">
          {journey.map((node) => {
            const completed = level >= node.maxLevel;
            const current = level >= node.minLevel && level <= node.maxLevel;
            return (
              <div key={node.name} className="flex items-center gap-3">
                <div className="relative">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-finley-full border-2 text-xs ${completed ? 'border-finley-success bg-finley-success/15 text-finley-success' : current ? 'border-finley-primary bg-finley-primary/15 text-finley-primary' : 'border-finley-text-muted/25 text-finley-text-light'}`}>
                    {completed ? '✓' : current ? '•' : '○'}
                  </div>
                  {current && <div className="absolute inset-[-5px] rounded-finley-full border-2 border-finley-primary/35" />}
                </div>
                <div className="flex-1">
                  <div className="font-finley-display text-base text-finley-text">{node.name}</div>
                  <div className="text-xs text-finley-text-light">Levels {node.minLevel}-{node.maxLevel}</div>
                </div>
                {completed && reachedDateByLevel[node.maxLevel] && (
                  <div className="text-xs text-finley-text-light">{new Date(reachedDateByLevel[node.maxLevel]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                )}
              </div>
            );
          })}
        </div>
        {!history.length && <p className="mt-3 text-xs text-finley-text-light">{/* TODO: show historical progression once level history is tracked for every level transition. */}Showing current position. Progress dates appear as more level transitions are recorded.</p>}
      </div>

      {hasSessions ? (
        <>
          <div className="mb-4 rounded-finley-xl border-2 border-finley-accent/20 bg-finley-bg-card p-5 shadow-finley-sm">
            <div className="mb-3 font-finley-display text-xl text-finley-text">Skill Mastery Breakdown</div>
            <div className="space-y-3">
              {modeMastery.map((row) => {
                const fillPct = Math.round(row.probability * 100);
                const fillColor = row.mastery.colorName === 'teal' ? MASTERY_COLORS.teal : row.mastery.colorName === 'deep-green' ? MASTERY_COLORS.green : MASTERY_COLORS.amber;
                return (
                  <div key={row.key}>
                    <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                      <span className="text-finley-text">{row.icon} {row.label}</span>
                      <span style={{ color: fillColor }} className="font-semibold">{row.mastery.label}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-finley-full bg-finley-bg">
                      <div className="h-full rounded-finley-full" style={{ width: `${fillPct}%`, background: fillColor }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-finley-xl border-2 border-finley-text-muted/15 bg-finley-bg-card p-5 shadow-finley-sm">
            <div className="mb-3 font-finley-display text-xl text-finley-text">Session History</div>
            <div className="space-y-3">
              {sessions.slice(0, 20).map((session) => {
                const key = String(session.day);
                const isOpen = !!expanded[key];
                const activitySummary = Object.entries(session.modeCounts)
                  .map(([mode, count]) => `${count} ${mode}`)
                  .join(', ');
                return (
                  <div key={session.day} className="rounded-finley-lg border border-finley-text-muted/15 bg-finley-bg p-3">
                    <button onClick={() => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))} className="w-full text-left">
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span className="font-semibold text-finley-text">{formatDay(session.day)} • {formatTime(session.last)}</span>
                        <span className="text-finley-text-light">{session.minutes} min</span>
                      </div>
                      <div className="text-xs text-finley-text-light">{activitySummary || 'Mixed activities'} • {session.accuracy}% accuracy</div>
                    </button>
                    {isOpen && (
                      <div className="mt-2 border-t border-finley-text-muted/10 pt-2 text-xs text-finley-text-light">
                        <div>Activities: {session.attempted}</div>
                        <div>Correct: {session.correct}</div>
                        {/* TODO: add mastery event badges when per-session mastery events are surfaced in session data. */}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {sessions.length > 20 && <button className="mt-3 text-sm font-semibold text-finley-primary underline decoration-transparent underline-offset-2 transition hover:decoration-finley-primary">View all</button>}
          </div>
        </>
      ) : (
        <div className="rounded-finley-xl border-2 border-finley-text-muted/15 bg-finley-bg-card p-5 text-center shadow-finley-sm">
          <p className="mb-3 text-sm text-finley-text-light">Complete your first session to start tracking progress! Finley will analyze each session and show your child's growth here.</p>
          <button onClick={onStartSession} className="min-h-[44px] rounded-finley-lg bg-finley-primary px-4 py-2 text-sm font-semibold text-white shadow-finley-sm">Start a Session</button>
        </div>
      )}
    </motion.div>
  );
}
